import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { TabComponent } from '@shared/components/tabs/tab/tab.component';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * A container component for rendering and managing a group of `app-tab` components.
 *
 * Handles tab activation, emits the currently active tab index, and ensures
 * only one tab is active at a time.
 *
 * Usage:
 * ```html
 * <app-tabs [(activeTab)]="selectedTab">
 *   <app-tab title="Tab 1">Content 1</app-tab>
 *   <app-tab title="Tab 2">Content 2</app-tab>
 * </app-tabs>
 * ```
 */
@Component({
  selector: 'app-tabs',
  imports: [
    NgClass,
    TranslatePipe
  ],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.css'
})
export class TabsComponent implements AfterViewInit, AfterContentInit, OnChanges {
  /**
   * List of projected `TabComponent` children.
   */
  @ContentChildren(TabComponent) public tabs?: TabComponent[];

  /**
   * Index of the currently active tab. Defaults to `0`.
   */
  @Input() public activeTab: number = 0;

  /**
   * Emits the index of the newly activated tab whenever the user switches tabs.
   */
  @Output() public activeTabChange: EventEmitter<number> = new EventEmitter<number>();

  /**
   * Lifecycle hook that runs after the component's content has been initialized.
   * Ensures the first tab is activated if none is set.
   */
  public ngAfterContentInit(): void {
    if (!this.tabs || this.tabs.length === 0) {
      return;
    }

    const firstTab = this.tabs[0];
    if (firstTab) {
      firstTab.activate();
    }
  }

  /**
   * Lifecycle hook that runs after the view has been fully initialized.
   * Ensures the `activeTab` index is respected and activated.
   * Uses a deferred call to avoid ExpressionChangedAfterItHasBeenCheckedError.
   */
  public ngAfterViewInit(): void {
    if (this.tabs) {
      // Deferred call to avoid call stack issues
      setTimeout(() => {
        if (this.tabs && this.tabs.length > 0) {
          [...this.tabs][this.activeTab].activate();
        }
      }, 10);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab'] && !changes['activeTab'].firstChange && this.tabs && this.tabs.length > 0) {
      const tab = this.tabs.find((element, index) => index === changes['activeTab'].currentValue);

      if (tab) {
        this.activateTab(tab);
      }
    }
  }

  /**
   * Activates a given tab and deactivates all others.
   * Emits `activeTabChange` with the index of the newly active tab.
   *
   * @param tab - The tab component to activate.
   */
  public activateTab(tab: TabComponent): void {
    if (this.tabs) {
      this.tabs.forEach((t: TabComponent) => t.deactivate());
      tab.activate();

      const index = [...this.tabs].indexOf(tab);
      this.activeTab = index;
      this.activeTabChange.emit(index);
    }
  }
}
